import type { AuthTokens } from '../types/auth';

// Token storage keys
const ACCESS_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';
const REMEMBER_ME_KEY = 'rememberMe';

// Get the appropriate storage based on remember me preference
const getStorage = (): Storage => {
  const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  return rememberMe ? localStorage : sessionStorage;
};

// Check if tokens exist in either storage
const getTokenFromBothStorages = (key: string): string | null => {
  return localStorage.getItem(key) || sessionStorage.getItem(key);
};

// Token storage utilities
export const tokenStorage = {
  // Get tokens
  getAccessToken: (): string | null => {
    return getTokenFromBothStorages(ACCESS_TOKEN_KEY);
  },

  getRefreshToken: (): string | null => {
    return getTokenFromBothStorages(REFRESH_TOKEN_KEY);
  },

  getTokens: (): AuthTokens | null => {
    const accessToken = getTokenFromBothStorages(ACCESS_TOKEN_KEY);
    const refreshToken = getTokenFromBothStorages(REFRESH_TOKEN_KEY);

    if (accessToken && refreshToken) {
      return { accessToken, refreshToken };
    }

    return null;
  },

  // Set tokens (uses appropriate storage based on remember me preference)
  setTokens: (tokens: AuthTokens, rememberMe?: boolean): void => {
    // If rememberMe is explicitly provided, update the preference
    if (rememberMe !== undefined) {
      if (rememberMe) {
        localStorage.setItem(REMEMBER_ME_KEY, 'true');
      } else {
        localStorage.removeItem(REMEMBER_ME_KEY);
      }
    }

    const storage = getStorage();

    // Clear tokens from the other storage to avoid conflicts
    const otherStorage =
      storage === localStorage ? sessionStorage : localStorage;
    otherStorage.removeItem(ACCESS_TOKEN_KEY);
    otherStorage.removeItem(REFRESH_TOKEN_KEY);
    otherStorage.removeItem(USER_KEY);

    // Set tokens in the appropriate storage
    storage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    storage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  },

  setAccessToken: (token: string): void => {
    const storage = getStorage();
    storage.setItem(ACCESS_TOKEN_KEY, token);
  },

  // Clear tokens from both storages
  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(REMEMBER_ME_KEY);

    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
  },

  // User data
  getUser: () => {
    const userData = getTokenFromBothStorages(USER_KEY);
    if (!userData) return null;

    try {
      return JSON.parse(userData);
    } catch (error) {
      console.warn('Invalid user data in storage, clearing...', error);
      // Clear invalid user data
      localStorage.removeItem(USER_KEY);
      sessionStorage.removeItem(USER_KEY);
      return null;
    }
  },

  setUser: (user: any): void => {
    const storage = getStorage();

    // Clear user from the other storage
    const otherStorage =
      storage === localStorage ? sessionStorage : localStorage;
    otherStorage.removeItem(USER_KEY);

    // Set user in the appropriate storage
    storage.setItem(USER_KEY, JSON.stringify(user));
  },

  clearUser: (): void => {
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(USER_KEY);
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const accessToken = getTokenFromBothStorages(ACCESS_TOKEN_KEY);
    return !!accessToken;
  },

  // Remember me utilities
  getRememberMe: (): boolean => {
    return localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  },

  setRememberMe: (remember: boolean): void => {
    if (remember) {
      localStorage.setItem(REMEMBER_ME_KEY, 'true');
    } else {
      localStorage.removeItem(REMEMBER_ME_KEY);
    }
  },
};
